import { showToast } from "@/components/feedback"
import { refusedCouponsMessages, unusableCouponCodes } from "@/lib/coupon-arrival"
import { getStoredCompany } from "@/lib/company-storage"
import type { CouponEvaluation } from "@/lib/discount-evaluation/types"
import { couponCodeKey, forgetKeptCoupons, savePendingCouponCodes } from "@/lib/pending-coupon"
import { atom, useStore } from "jotai"
import { checkoutAtom } from "../state"

/** Codes applied in the last verdict, so a coupon that starts to apply is announced once. */
export const lastAppliedCouponsAtom = atom<string[]>([])

function outsideWindowCodes(evaluation: CouponEvaluation): string[] {
  return evaluation.rejected.filter((rejected) => rejected.reason === "EVALUATION_REASON_OUTSIDE_AVAILABILITY").map((rejected) => rejected.code)
}

/**
 * What a fresh coupon verdict changes in the checkout: a code refused for
 * good leaves the order and the wallet, told once in one toast; one that lost
 * to a better combination or is only outside its weekly window leaves the
 * order but stays in the wallet; a code that just started to apply is
 * confirmed once.
 */
export function useSettleCouponCodes(): (evaluation: CouponEvaluation) => void {
  const store = useStore()

  return (evaluation) => {
    const { refused, outranked } = unusableCouponCodes(evaluation, store.get(checkoutAtom).couponCodes)
    const leaving = new Set([...refused, ...outranked, ...outsideWindowCodes(evaluation)].map(couponCodeKey))
    if (leaving.size > 0) {
      store.set(checkoutAtom, (prev) => ({ ...prev, couponCodes: prev.couponCodes.filter((code) => !leaving.has(couponCodeKey(code))) }))
      const merchantId = getStoredCompany()?.id
      if (merchantId) {
        savePendingCouponCodes(store.get(checkoutAtom).couponCodes, merchantId)
        forgetKeptCoupons(refused, merchantId)
      }
      for (const message of refusedCouponsMessages(evaluation, refused)) showToast.info(message)
    }

    const applied = evaluation.applied.map((coupon) => coupon.code)
    const before = new Set(store.get(lastAppliedCouponsAtom))
    for (const code of applied) {
      if (!before.has(code)) showToast.success(`Cupom ${code} aplicado!`)
    }
    store.set(lastAppliedCouponsAtom, applied)
  }
}
